/* =========================================================================
 * POSTSEASON RUNS — the opportunities that are NOT a title.
 *
 * Brian: "It's not the only place where a loser gets a ring — think about the
 * Final Four, Sweet 16, teams still get rings in other sports if they make big
 * tournaments as it's a major accomplishment. I want you to solidify all of the
 * opportunities and make sure that every single one of them is logged."
 *
 * ⚠️ THIS IS A CORRECTION TO THE MODEL, NOT MORE ROWS OF THE SAME THING.
 * The object recorded CHAMPIONSHIPS: one title, one winner. That quietly encoded
 * an assumption that only winning is commemorated — and it is wrong. A Final
 * Four team orders rings. So does a College World Series team, a Frozen Four
 * team, a Sweet 16 team. Under the old model every one of those was invisible.
 *
 * And the arithmetic is the point. A championship is ONE customer. The rounds
 * below reach 64, 32, 16, 8, 4 — the same tournament is dozens of orders, and
 * `teams` is carried in the name because it is the commercially important
 * number, not a footnote.
 *
 * ⚠️ A ROUND IS NOT A CHAMPIONSHIP, so `sr_level: 'Tournament Round'` keeps them
 * structurally separable — the same trick that lets Bowl Season filter cleanly.
 * Without it "14,211 championships" quietly stops meaning championships.
 *
 * ⚠️ THE NAMES ARE TRADEMARKS AND THE OFFICIAL FORM MATTERS.
 *   · "Sweet 16" is owned by the KENTUCKY HIGH SCHOOL ATHLETIC ASSOCIATION, not
 *     the NCAA. The NCAA registered "NCAA Sweet 16" with KHSAA's consent, so the
 *     licensed form carries the NCAA prefix — bare "Sweet 16" is somebody else's
 *     mark, and we are a manufacturer who puts these words on metal.
 *   · Final Four®, Women's Final Four®, Elite Eight®, Frozen Four®, First Four®,
 *     March Madness® are NCAA marks. The women's round has its OWN registered
 *     form: "Women's Final Four", never "Final Four".
 *   · Naming a round on a ring is third-party-trademark territory and belongs in
 *     the same licensing lane as a school crest.
 *
 * ⚠️ ROUNDS WITH NO BRAND ARE STILL RECORDED, deliberately. "First Round" and
 * "Quarterfinals" carry no marketing brand a school would engrave, but reaching
 * them is still the accomplishment that starts the conversation — and Brian
 * asked for every opportunity logged, not only the famous ones. What is NOT
 * done is inventing a brand for them: media shorthand like "Round of 64" and a
 * volleyball "Final Four" the NCAA does not use are recorded under the NCAA's
 * own round names instead.
 * ========================================================================= */

const SRC = {
  bb: 'https://www.ncaa.org/championships/march-madness/expansion-what-to-know/',
  base: 'https://www.ncaa.com/news/baseball/article/2025-06-10/how-mens-college-world-series-works',
  sb: 'https://www.ncaa.com/championships/softball/d1',
  hk: 'https://www.ncaa.com/championships/icehockey-men/d1/road-to-the-championship',
  vb: 'https://www.ncaa.org/championship/division-i/womens-volleyball/',
  soc: 'https://www.ncaa.com/championships/soccer-men/d1/road-to-the-championship',
  fb: 'https://www.ncaa.com/championships/football/fcs',
  lax: 'https://www.ncaa.org/championships/',
}

/**
 * `teams` rides in the DIVISION label, which is what the board renders. A round
 * is only worth working if you know how many schools reach it, and burying that
 * in a field nobody displays would waste the most useful number here.
 *
 * ⚠️ THE NCAA DIVISION IS PART OF THE LABEL, and that is a bug fix, not a style
 * choice. The first version of this helper TOOK a `division` argument and then
 * never used it — the round went into `divisions` on its own. So DI-FCS,
 * Division II and Division III football all produced the identical record
 * "NCAA Football Men Quarterfinals (8 teams)" and collapsed onto ONE
 * deterministic key. Three separate championships' worth of postseason, five
 * duplicate keys, caught only because the catalog is checked for key collisions
 * before anything is created.
 *
 * The failure mode is the one that keeps recurring here: a value was passed in,
 * looked handled, and was silently dropped. An argument a helper accepts and
 * ignores is worse than one it never asked for.
 */
const R = (sport, gender, division, round, teams, source) => ({
  st: 'US', state: '', body: 'NCAA', sport, gender,
  divisions: [`${division} ${round} (${teams} teams)`],
  source, segment: 'College', level: 'Tournament Round',
  season: '2026', activity: false, note: '',
})

/* ⚠️ BASKETBALL EXPANDS TO 76 TEAMS FOR 2026-27 (first played March 2027),
 * approved unanimously 2026-05-07. The First Four (8 teams) is REPLACED by an
 * Opening Round of 24. Every round from the First Round down is unchanged in
 * size. Recorded as the NEW structure because the 2027 tournament is the one a
 * rep will work; anything SR recorded for March 2026 or earlier was a 68-team
 * field with a First Four, and that is a different record, not this one. */
const BASKETBALL = [
  ['Men', 'Opening Round', 24], ['Men', 'First Round', 64], ['Men', 'Second Round', 32],
  ['Men', 'NCAA Sweet 16', 16], ['Men', 'Elite Eight', 8], ['Men', 'Final Four', 4],
  ['Men', 'National Championship Game', 2],
  ['Women', 'Opening Round', 24], ['Women', 'First Round', 64], ['Women', 'Second Round', 32],
  ['Women', 'NCAA Sweet 16', 16], ['Women', 'Elite Eight', 8], ['Women', "Women's Final Four", 4],
  ['Women', 'National Championship Game', 2],
]

const ROUNDS = [
  ...BASKETBALL.map(([g, r, n]) => R('Basketball', g, 'Division I', r, n, SRC.bb)),

  /* Omaha and Oklahoma City are SITES, not rounds. The rounds are the College
   * World Series itself — 8 teams each. */
  R('Baseball', 'Men', 'Division I', 'Regionals', 64, SRC.base),
  R('Baseball', 'Men', 'Division I', 'Super Regionals', 16, SRC.base),
  R('Baseball', 'Men', 'Division I', "Men's College World Series", 8, SRC.base),
  R('Baseball', 'Men', 'Division I', "Men's College World Series Finals", 2, SRC.base),

  R('Softball', 'Women', 'Division I', 'Regionals', 64, SRC.sb),
  R('Softball', 'Women', 'Division I', 'Super Regionals', 16, SRC.sb),
  R('Softball', 'Women', 'Division I', "Women's College World Series", 8, SRC.sb),
  R('Softball', 'Women', 'Division I', 'WCWS Championship Series', 2, SRC.sb),

  R('Ice Hockey', 'Men', 'Division I', 'Regional Semifinals', 16, SRC.hk),
  R('Ice Hockey', 'Men', 'Division I', 'Regional Finals', 8, SRC.hk),
  R('Ice Hockey', 'Men', 'Division I', "Men's Frozen Four", 4, SRC.hk),
  R('Ice Hockey', 'Men', 'Division I', 'National Championship Game', 2, SRC.hk),
  R('Ice Hockey', 'Women', 'Division I', 'First Round', 11, SRC.hk),
  R('Ice Hockey', 'Women', 'Division I', 'Quarterfinals', 8, SRC.hk),
  R('Ice Hockey', 'Women', 'Division I', "Women's Frozen Four", 4, SRC.hk),
  R('Ice Hockey', 'Women', 'Division I', 'National Championship Game', 2, SRC.hk),

  /* ⚠️ NO "VOLLEYBALL FINAL FOUR". The NCAA calls it the National Semifinals;
   * "Final Four" here is media and AVCA usage, and putting an unlicensed brand
   * on a ring is exactly the mistake this file exists to avoid. */
  R('Volleyball', 'Women', 'Division I', 'First Round', 64, SRC.vb),
  R('Volleyball', 'Women', 'Division I', 'Second Round', 32, SRC.vb),
  R('Volleyball', 'Women', 'Division I', 'Regional Semifinals', 16, SRC.vb),
  R('Volleyball', 'Women', 'Division I', 'Regional Finals', 8, SRC.vb),
  R('Volleyball', 'Women', 'Division I', 'National Semifinals', 4, SRC.vb),
  R('Volleyball', 'Women', 'Division I', 'National Championship Match', 2, SRC.vb),

  /* ⚠️ "College Cup" is the semifinals and final ONLY — 4 teams. A quarterfinal
   * team has NOT made the College Cup, and selling them one would be wrong. */
  R('Soccer', 'Men', 'Division I', 'First Round', 48, SRC.soc),
  R('Soccer', 'Men', 'Division I', 'Second Round', 32, SRC.soc),
  R('Soccer', 'Men', 'Division I', 'Third Round', 16, SRC.soc),
  R('Soccer', 'Men', 'Division I', 'Quarterfinals', 8, SRC.soc),
  R('Soccer', 'Men', 'Division I', "Men's College Cup", 4, SRC.soc),
  R('Soccer', 'Men', 'Division I', 'National Championship Match', 2, SRC.soc),
  R('Soccer', 'Women', 'Division I', 'First Round', 64, SRC.soc),
  R('Soccer', 'Women', 'Division I', 'Second Round', 32, SRC.soc),
  R('Soccer', 'Women', 'Division I', 'Third Round', 16, SRC.soc),
  R('Soccer', 'Women', 'Division I', 'Quarterfinals', 8, SRC.soc),
  R('Soccer', 'Women', 'Division I', "Women's College Cup", 4, SRC.soc),
  R('Soccer', 'Women', 'Division I', 'National Championship Match', 2, SRC.soc),

  R('Football', 'Men', 'Division I FCS', 'First Round', 24, SRC.fb),
  R('Football', 'Men', 'Division I FCS', 'Second Round', 16, SRC.fb),
  R('Football', 'Men', 'Division I FCS', 'Quarterfinals', 8, SRC.fb),
  R('Football', 'Men', 'Division I FCS', 'Semifinals', 4, SRC.fb),
  R('Football', 'Men', 'Division II', 'First Round', 32, SRC.fb),
  R('Football', 'Men', 'Division II', 'Second Round', 16, SRC.fb),
  R('Football', 'Men', 'Division II', 'Quarterfinals', 8, SRC.fb),
  R('Football', 'Men', 'Division II', 'Semifinals', 4, SRC.fb),
  R('Football', 'Men', 'Division III', 'First Round', 40, SRC.fb),
  R('Football', 'Men', 'Division III', 'Second Round', 32, SRC.fb),
  R('Football', 'Men', 'Division III', 'Quarterfinals', 8, SRC.fb),
  R('Football', 'Men', 'Division III', 'Semifinals', 4, SRC.fb),
  /* The Stagg Bowl is the DIII championship GAME and the only branded round in
   * college football's playoffs. Not repeated as a separate title — the DIII
   * football championship record already exists. */

  R('Lacrosse', 'Men', 'Division I', 'Opening Round', 18, SRC.lax),
  R('Lacrosse', 'Men', 'Division I', 'First Round', 16, SRC.lax),
  R('Lacrosse', 'Men', 'Division I', 'Quarterfinals', 8, SRC.lax),
  R('Lacrosse', 'Men', 'Division I', 'Semifinals', 4, SRC.lax),
  R('Lacrosse', 'Women', 'Division I', 'First Round', 29, SRC.lax),
  R('Lacrosse', 'Women', 'Division I', 'Second Round', 16, SRC.lax),
  R('Lacrosse', 'Women', 'Division I', 'Quarterfinals', 8, SRC.lax),
  R('Lacrosse', 'Women', 'Division I', 'Semifinals', 4, SRC.lax),
]

/* ⚠️ DIII FOOTBALL'S ROUND OF 16 IS DELIBERATELY ABSENT. The 40-team bracket
 * demonstrably has one — 16 play the first round, 24 bye, five Saturdays then a
 * January final — but no NCAA source states its official NAME. "Third Round" is
 * an inference, and inventing a round name is the same error as inventing a
 * championship. It is a gap we can name. */

export const GAPS_ROUNDS = ROUNDS
